/**
 * The permanent Delete's three permissions, as the screen words them (owner, 27 Oct 2026: "we need trash
 * and delete. But if the permission doesn't have it ... we can say that it can't be done, as permission is
 * not there. But don't remove it.").
 *
 * The API asks the permissions in ONE place (apps/api/src/modules/er/services/er-delete-gate.ts) and
 * refuses with 403 `er_delete_permission_missing` and a `reason`. This is the screen's mirror, so a
 * control can say WHY it is off before the click (DisabledWithReason) and the same sentence comes back
 * when the API refuses anyway. The API is the boundary; this is guidance, and like every role-aware
 * control it fails open: only a KNOWN "no" disables (a policy still loading is not a "no").
 *
 *   role            the person lacks delete:ErHardDelete
 *   switch          the workspace's hard-delete switch is off
 *   provider_scope  Google has not granted permanent delete (https://mail.google.com/ is not requested)
 *
 * Trash is not behind any of this: it needs only the ordinary remediation ability.
 */
export type ErDeleteBlockedReason = 'role' | 'switch' | 'provider_scope'

/** The API's errorCode for a Delete that is missing a permission. */
export const ER_DELETE_PERMISSION_MISSING = 'er_delete_permission_missing'

const BLOCKED_KEYS: Readonly<Record<ErDeleteBlockedReason, string>> = {
  role: 'er.delete.blocked.role',
  switch: 'er.delete.blocked.switch',
  // The sentence that needs no domain (an API refusal does not name one): see erDeleteBlockedText.
  provider_scope: 'er.delete.blocked.providerScopeUnknown',
}

/** "Google has not granted permanent delete for {domain}": the sentence for a control that knows the domain. */
export const ER_DELETE_BLOCKED_PROVIDER_DOMAIN_KEY = 'er.delete.blocked.providerScope'

/** Said when the API names a reason this screen does not know. */
export const ER_DELETE_BLOCKED_GENERIC_KEY = 'er.delete.blocked.generic'

export function isDeleteBlockedReason(value: unknown): value is ErDeleteBlockedReason {
  return value === 'role' || value === 'switch' || value === 'provider_scope'
}

/** The catalogue key of a blocked reason, a sentence that takes no parameter (what an API refusal can say). */
export function erDeleteBlockedKey(reason: unknown): string {
  return isDeleteBlockedReason(reason) ? BLOCKED_KEYS[reason] : ER_DELETE_BLOCKED_GENERIC_KEY
}

/**
 * The sentence of a blocked reason in the interface language, naming the domain when a Google mailbox's is
 * known ("Can't be done: Google has not granted permanent delete for acme.ae").
 */
export function erDeleteBlockedText(
  t: (key: string, params?: Record<string, string | number>) => string,
  reason: unknown,
  domain?: string | null,
): string {
  if (reason === 'provider_scope' && domain) return t(ER_DELETE_BLOCKED_PROVIDER_DOMAIN_KEY, { domain })
  return t(erDeleteBlockedKey(reason))
}

/** The part of a mailbox address after the @, for "Google has not granted permanent delete for {domain}". */
export function mailboxDomain(email: string | null | undefined): string | null {
  const at = email ? email.lastIndexOf('@') : -1
  return at > 0 && email ? email.slice(at + 1).trim().toLowerCase() || null : null
}

/**
 * The first missing permission, in the order a person can act on it (the API's order), or null when
 * Delete may be tried. `hardDeleteEnabled` undefined = the policy is not known yet: not a "no".
 */
export function erDeleteBlocked(input: {
  /** denied === true from usePermissionGate('delete', 'ErHardDelete'). */
  roleDenied: boolean
  hardDeleteEnabled: boolean | undefined
  /** The mail is in a Google mailbox. */
  google: boolean
}): ErDeleteBlockedReason | null {
  if (input.roleDenied) return 'role'
  if (input.hardDeleteEnabled === false) return 'switch'
  if (input.google) return 'provider_scope'
  return null
}

/**
 * The `reason` of an er_delete_permission_missing refusal, or null when `error` is not one. Read off
 * the body structurally, so it also works where a screen's API client is a test double.
 */
export function deleteRefusalReason(error: unknown): ErDeleteBlockedReason | 'unknown' | null {
  const e = error as { errorCode?: unknown; body?: unknown } | null
  if (!e || e.errorCode !== ER_DELETE_PERMISSION_MISSING) return null
  const body = e.body
  const reason = body && typeof body === 'object' ? (body as { reason?: unknown }).reason : undefined
  return isDeleteBlockedReason(reason) ? reason : 'unknown'
}
